import { graphql, readInlineData, type Environment } from 'react-relay';

import { refreshSfChatToken } from '../../helpers/refreshSfChatToken.ts';

import { getSierraConfig } from './getSierraConfig.ts';
import { updateSierraVariables } from './updateSierraVariables.ts';
import { updateSierraSecrets } from './updateSierraSecrets.ts';

import { type IntlShape } from 'dibs-react-intl';
import { type SupportChatTouchpoint, type PreChatFields } from '../../types.ts';
import { type initSierraChat_user$key } from './__generated__/initSierraChat_user.graphql.ts';
import { type initSierraChat_seller$key } from './__generated__/initSierraChat_seller.graphql.ts';

const userFragment = graphql`
    fragment initSierraChat_user on User @inline {
        serviceId
        token
        emailToken
        sfChatToken
    }
`;

const sellerFragment = graphql`
    fragment initSierraChat_seller on Seller @inline {
        serviceId
        token
        sfChatToken
    }
`;

type InitSierraOptions = {
    environment: Environment;
    user: initSierraChat_user$key | null | undefined;
    seller: initSierraChat_seller$key | null | undefined;
    isSellerChat: boolean;
    touchpoint: SupportChatTouchpoint;
    locale: IntlShape['locale'];
    isLiveAgentAvailable: boolean;
    preChatFields: PreChatFields;
    openWindow: boolean;
    setIsInitialized: (isInitialized: boolean) => void;
};

function initSierraChat({
    environment,
    user: userRef,
    seller: sellerRef,
    isSellerChat,
    openWindow,
    setIsInitialized,
    ...options
}: InitSierraOptions): void {
    const user = readInlineData(userFragment, userRef);
    const seller = readInlineData(sellerFragment, sellerRef);

    let userId = '';
    let userToken: string | null | undefined = null;
    let sellerId = '';
    let sellerToken: string | null | undefined = null;
    let sfChatToken: string | null | undefined = null;

    if (isSellerChat) {
        sellerId = seller?.serviceId ?? '';
        sellerToken = seller?.token;
        sfChatToken = seller?.sfChatToken;
    } else {
        userId = user?.serviceId ?? '';
        userToken = user?.token || user?.emailToken;
        sfChatToken = user?.sfChatToken;
    }

    const isSierraChatInitialized = window.sierra?.isInitialized?.();

    if (isSierraChatInitialized) {
        setIsInitialized(true);
    }

    if (isSierraChatInitialized) {
        updateSierraVariables({
            touchpoint: options.touchpoint,
            isLiveAgentAvailable: options.isLiveAgentAvailable,
            preChatFields: options.preChatFields,
        });
        updateSierraSecrets({
            userToken,
            sellerToken,
            sfChatToken,
        });
    } else {
        window.sierra?.init?.(
            getSierraConfig({
                ...options,
                userToken,
                sellerToken,
                sfChatToken,
                onSfChatTokenRefresh: () =>
                    refreshSfChatToken({
                        environment,
                        ...(isSellerChat ? { sellerId } : { userId }),
                    }),
            })
        );
    }

    if (openWindow) {
        window.sierra?.openChatModal?.();
    }
}

export { initSierraChat, type InitSierraOptions };
